/**
 * UR Brand System — Phase 2 question renderers (shared).
 *
 * One renderer per supported question type. Each receives:
 *   { question, value, allowUnknown, onChange }
 *
 * Every renderer uses the existing shadcn UI primitives + Tailwind so
 * the shell inherits the Ur Designs look without a bespoke redesign.
 */
import React, { useState } from "react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";

const UNKNOWN = "unknown_stated";

const isUnknown = (v) => v === UNKNOWN;
const unwrap = (v) => (v && typeof v === "object" && "value" in v ? v.value : v);

const OptionButton = ({ active, children, onClick, testid }) => (
  <button
    type="button"
    data-testid={testid}
    data-active={active ? "true" : "false"}
    onClick={onClick}
    className={`w-full text-left px-4 py-3 rounded-lg border transition
      ${active
        ? "border-orange-500 bg-orange-500/10 text-white"
        : "border-neutral-700 bg-neutral-900 hover:border-neutral-500 text-neutral-200"}`}
  >
    {children}
  </button>
);

const UnknownButton = ({ active, onClick }) => (
  <button
    type="button"
    data-testid="intake-unknown"
    data-active={active ? "true" : "false"}
    onClick={onClick}
    className={`mt-3 px-3 py-2 text-xs rounded-md border uppercase tracking-wider
      ${active
        ? "border-orange-500 bg-orange-500/15 text-orange-200"
        : "border-neutral-700 bg-neutral-900 hover:border-neutral-500 text-neutral-400"}`}
  >
    I don't know yet
  </button>
);

// --- renderers -------------------------------------------------------------

export const Informational = ({ question }) => (
  <div className="text-neutral-300 text-base leading-relaxed">
    {question.help}
  </div>
);

export const ShortText = ({ question, value, onChange, allowUnknown }) => {
  const v = unwrap(value);
  const isUnk = isUnknown(v);
  return (
    <div>
      <Input
        data-testid={`intake-input-${question.id}`}
        type={question.inputType || "text"}
        value={isUnk ? "" : (v || "")}
        onChange={(e) => onChange(e.target.value)}
        disabled={isUnk}
        className="bg-neutral-900 border-neutral-700 text-white"
      />
      {allowUnknown && (
        <UnknownButton active={isUnk} onClick={() => onChange(isUnk ? "" : UNKNOWN)} />
      )}
    </div>
  );
};

export const LongText = ({ question, value, onChange, allowUnknown }) => {
  const v = unwrap(value);
  const isUnk = isUnknown(v);
  return (
    <div>
      <Textarea
        data-testid={`intake-input-${question.id}`}
        value={isUnk ? "" : (v || "")}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        disabled={isUnk}
        className="bg-neutral-900 border-neutral-700 text-white"
      />
      {allowUnknown && (
        <UnknownButton active={isUnk} onClick={() => onChange(isUnk ? "" : UNKNOWN)} />
      )}
    </div>
  );
};

export const SingleSelect = ({ question, value, onChange, allowUnknown }) => {
  const v = unwrap(value);
  const isUnk = isUnknown(v);
  return (
    <div className="space-y-2">
      {question.options.map((opt) => (
        <OptionButton
          key={String(opt.value)}
          testid={`intake-opt-${question.id}-${opt.value}`}
          active={!isUnk && v === opt.value}
          onClick={() => onChange(opt.value)}
        >
          <div className="font-medium">{opt.label}</div>
          {opt.help && <div className="text-xs text-neutral-400 mt-1">{opt.help}</div>}
        </OptionButton>
      ))}
      {allowUnknown && (
        <UnknownButton active={isUnk} onClick={() => onChange(isUnk ? null : UNKNOWN)} />
      )}
    </div>
  );
};

export const MultiSelect = ({ question, value, onChange, allowUnknown }) => {
  const raw = unwrap(value);
  const isUnk = isUnknown(raw);
  const list = Array.isArray(raw) ? raw : [];
  const max = question.maxSelections;
  const toggle = (val) => {
    if (isUnk) return;
    if (list.includes(val)) {
      onChange(list.filter((v) => v !== val));
    } else {
      if (max && list.length >= max) return;
      onChange([...list, val]);
    }
  };
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {question.options.map((opt) => (
          <OptionButton
            key={String(opt.value)}
            testid={`intake-opt-${question.id}-${opt.value}`}
            active={!isUnk && list.includes(opt.value)}
            onClick={() => toggle(opt.value)}
          >
            {opt.label}
          </OptionButton>
        ))}
      </div>
      {max && (
        <div className="text-xs text-neutral-500 mt-2" data-testid={`intake-cap-${question.id}`}>
          {list.length}/{max} selected
        </div>
      )}
      {allowUnknown && (
        <UnknownButton active={isUnk} onClick={() => onChange(isUnk ? [] : UNKNOWN)} />
      )}
    </div>
  );
};

/** Free-form tag input backed by an array value. Enter adds a tag. */
export const MultiSelectText = ({ question, value, onChange, allowUnknown }) => {
  const [draft, setDraft] = useState("");
  const raw = unwrap(value);
  const isUnk = isUnknown(raw);
  const list = Array.isArray(raw) ? raw : [];
  const add = () => {
    const t = draft.trim();
    if (!t) return;
    if (list.includes(t)) { setDraft(""); return; }
    onChange([...list, t]);
    setDraft("");
  };
  const remove = (t) => onChange(list.filter((v) => v !== t));
  return (
    <div>
      <div className="flex gap-2">
        <Input
          data-testid={`intake-input-${question.id}`}
          value={draft}
          disabled={isUnk}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Press Enter after each"
          className="bg-neutral-900 border-neutral-700 text-white"
        />
        <Button type="button" variant="secondary" onClick={add} disabled={isUnk}>Add</Button>
      </div>
      {list.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {list.map((t) => (
            <span
              key={t}
              data-testid={`intake-tag-${question.id}-${t}`}
              className="inline-flex items-center gap-2 px-2 py-1 text-xs rounded-md bg-neutral-800 text-neutral-200 border border-neutral-700"
            >
              {t}
              <button type="button" onClick={() => remove(t)} className="text-neutral-500 hover:text-white">×</button>
            </span>
          ))}
        </div>
      )}
      {allowUnknown && (
        <UnknownButton active={isUnk} onClick={() => onChange(isUnk ? [] : UNKNOWN)} />
      )}
    </div>
  );
};

export const PerceptionScale = ({ question, value, onChange, allowUnknown }) => {
  const v = unwrap(value);
  const isUnk = isUnknown(v);
  const options = [
    { value: "left", label: "Left" },
    { value: "balanced", label: "Balanced" },
    { value: "right", label: "Right" },
  ];
  return (
    <div>
      <div className="grid grid-cols-3 gap-2">
        {options.map((opt) => (
          <OptionButton
            key={opt.value}
            testid={`intake-opt-${question.id}-${opt.value}`}
            active={!isUnk && v === opt.value}
            onClick={() => onChange(opt.value)}
          >
            <div className="text-center text-sm">{opt.label}</div>
          </OptionButton>
        ))}
      </div>
      {allowUnknown && (
        <UnknownButton active={isUnk} onClick={() => onChange(isUnk ? null : UNKNOWN)} />
      )}
    </div>
  );
};

export const DifferentiationReason = ({ question, value, onChange, allowUnknown }) => {
  const v = value || {};
  const sel = unwrap(v.selection);
  const det = unwrap(v.detail);
  const prf = unwrap(v.proof);
  const isUnk = isUnknown(sel);
  const update = (key, val) => onChange({ ...v, [key]: val });
  return (
    <div className="space-y-4">
      <div>
        <Label className="text-neutral-300 text-xs uppercase tracking-wider">Reason</Label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
          {question.options.map((opt) => (
            <OptionButton
              key={opt.value}
              testid={`intake-opt-${question.id}-${opt.value}`}
              active={!isUnk && sel === opt.value}
              onClick={() => update("selection", opt.value)}
            >
              {opt.label}
            </OptionButton>
          ))}
        </div>
      </div>
      {!isUnk && sel && (
        <>
          <div>
            <Label className="text-neutral-300 text-xs uppercase tracking-wider">Say more</Label>
            <Textarea
              data-testid={`intake-input-${question.id}-detail`}
              value={det || ""}
              onChange={(e) => update("detail", e.target.value)}
              rows={2}
              className="bg-neutral-900 border-neutral-700 text-white mt-2"
            />
          </div>
          <div>
            <Label className="text-neutral-300 text-xs uppercase tracking-wider">
              Any proof? (reviews, repeat customers, numbers)
            </Label>
            <Textarea
              data-testid={`intake-input-${question.id}-proof`}
              value={prf || ""}
              onChange={(e) => update("proof", e.target.value)}
              rows={2}
              className="bg-neutral-900 border-neutral-700 text-white mt-2"
            />
          </div>
        </>
      )}
      {allowUnknown && (
        <UnknownButton
          active={isUnk}
          onClick={() => onChange(isUnk ? {} : { selection: UNKNOWN })}
        />
      )}
    </div>
  );
};

export const Competitors = ({ question, value, onChange }) => {
  const raw = unwrap(value);
  const list = Array.isArray(raw) ? raw : [];
  const max = question.maxEntries || 3;
  const update = (i, patch) => {
    const next = list.slice();
    next[i] = { ...(next[i] || {}), ...patch };
    onChange(next);
  };
  const add = () => {
    if (list.length >= max) return;
    onChange([...list, { name: "" }]);
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-4">
      {list.map((c, i) => (
        <div key={i} className="p-3 border border-neutral-800 rounded-lg space-y-2">
          <Input
            data-testid={`intake-competitor-${i}-name`}
            placeholder="Competitor name"
            value={unwrap(c.name) || ""}
            onChange={(e) => update(i, { name: e.target.value })}
            className="bg-neutral-900 border-neutral-700 text-white"
          />
          <Input
            data-testid={`intake-competitor-${i}-website`}
            placeholder="Website (optional)"
            value={unwrap(c.website) || ""}
            onChange={(e) => update(i, { website: e.target.value })}
            className="bg-neutral-900 border-neutral-700 text-white"
          />
          <Input
            data-testid={`intake-competitor-${i}-social`}
            placeholder="Social URL (optional)"
            value={unwrap(c.social_url) || ""}
            onChange={(e) => update(i, { social_url: e.target.value })}
            className="bg-neutral-900 border-neutral-700 text-white"
          />
          <button
            type="button"
            onClick={() => remove(i)}
            className="text-xs text-neutral-500 hover:text-white"
            data-testid={`intake-competitor-${i}-remove`}
          >
            Remove
          </button>
        </div>
      ))}
      {list.length < max && (
        <Button type="button" variant="secondary" onClick={add} data-testid={`intake-competitor-add`}>
          + Add competitor ({list.length}/{max})
        </Button>
      )}
    </div>
  );
};
